/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { Roles } from '../../../../permissions/defs.js';

import KenticoStore from 'stores/KenticoStore';
import UserStore from 'stores/UserStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import Loading from 'components/Loading/Loading';

import styles from './FeaturedProduct.module.scss';

type InjectedProps = {
  kenticoStore: KenticoStore,
  userStore: UserStore,
} & TranslatorProps;

/**
 * Checks if the given promotion is the CAAS promotion.
 */
function isCAASPromotion(promotion) {
  return promotion.system.codename === 'caas_portal_promotion';
}

/**
 * Checks if the given role is a System Admin.
 */
function isSystemAdmin(role_id) {
  return role_id === Roles.SYSTEM_ADMIN;
}

/**
 * Checks if the given country is allowed to see CaaS ad banners.
 */
function isCaaSAllowedCountry(code: string) {
  return code === 'US' || code === 'CA';
}

/**
 * Returns a value that can be pass as the "to" property of a Link component.
 * If the given promotion is the CAAS promotion it will return an object
 * with the necessary information to send the user to the Support Form
 * and automatically select the "Calibration as a Service" option.
 * If the given promotion is regular, ctaLink.value is returned.
 */
function getPromotionLink(promotion) {
  if (!isCAASPromotion(promotion)) {
    return promotion.ctaLink.value;
  }

  return {
    pathname: promotion.ctaLink.value,
    state: { id: 'Calibration as a Service' },
  };
}

@translate()
@inject('kenticoStore', 'userStore')
@observer
export default class FeaturedProduct extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  renderCardContent = () => {
    const {
      userStore: { activeUser },
      kenticoStore: { portalPromotionsCarousel },
      t,
    } = this.props;
    if (portalPromotionsCarousel.loading || !activeUser) {
      return <Loading />;
    }

    if (
      !portalPromotionsCarousel.loading &&
      (!portalPromotionsCarousel.content ||
        portalPromotionsCarousel.content.promotions.value.length === 0)
    ) {
      return (
        <div className={styles.emptyStateWrapper}>
          <svg>
            <use xlinkHref="#icon-beach-access" />
          </svg>
          <h2>{t('general:no_promotions.title')}</h2>
          <h3>{t('general:no_promotions.body')}</h3>
        </div>
      );
    }

    /*
     * Promotions Filtering
     * -------------------------------------------------------------------------
     * Some promotions are not available for all users, in the next section we
     * filter promotions by some rules, like promotions for specific trading
     * accounts, or CAAS promotion which is only available for users located
     * in US or Canada.
     *
     */
    const displayablePromotions = [];
    const { countryCode, role_id } = activeUser;

    if (portalPromotionsCarousel.content && activeUser) {
      for (const promotion of portalPromotionsCarousel.content.promotions.value) {
        // CaaS Promotion must have "caas_portal_promotion" as its codename property.
        // Skip CaaS Promotion if the current user is NOT a System Admin and their
        // country is NOT set to US or Canada.
        if (
          isCAASPromotion(promotion) &&
          !isSystemAdmin(role_id) &&
          !isCaaSAllowedCountry(countryCode)
        ) {
          continue;
        }

        // When the current promotion does not have any specific trading account we
        // can safely add it to the list of displayable promotions.
        if (promotion.tradingAccountIds.length === 0) {
          displayablePromotions.push(promotion);
          continue;
        }

        // In the case current promotion is set for specific trading accounts we
        // check against the current user accounts, if at least one matches we
        // can safely add ti to the list of displayable promotions.
        const availableForTradingAccount = promotion.tradingAccountIds.some(tradingAccountId =>
          activeUser.accounts.some(activeUserAccount => activeUserAccount.id === tradingAccountId)
        );

        if (availableForTradingAccount) {
          displayablePromotions.push(promotion);
        }
      }
    }

    return (
      <Carousel showThumbs={false} showStatus={false} autoPlay infiniteLoop interval={10000}>
        {displayablePromotions.map(promotion => {
          if (typeof promotion !== 'object') {
            return null;
          }

          return (
            <Link
              key={promotion.system.id}
              rel="noopener noreferrer"
              to={getPromotionLink(promotion)}
              target={promotion.ctaLink.value.startsWith('http') ? '__blank' : undefined}
            >
              <div className={styles.carouselSlide}>
                <div className={styles.carouselSlideContentContainer}>
                  <div
                    dangerouslySetInnerHTML={{ __html: promotion.copy.value }}
                    className={`
                      ${styles.carouselSlideContent}
                      ${
                        promotion.contentTheme.value.some(value => value.codename === 'light')
                          ? styles.light
                          : styles.dark
                      }
                    `}
                  />
                  <div className={styles.carouselSlideActionWrapper}>
                    {promotion.ctaLabel.value && (
                      <Button variant="contained" color="primary">
                        {promotion.ctaLabel.value}
                      </Button>
                    )}
                  </div>
                </div>

                {promotion.backgroundImage.value.length > 0 && (
                  <div
                    className={styles.carouselSlideBackgroundContainer}
                    style={{
                      backgroundImage: `url(${promotion.backgroundImage.value[0].url})`,
                    }}
                  />
                )}
              </div>
            </Link>
          );
        })}
      </Carousel>
    );
  };

  render() {
    const { t } = this.props;

    return (
      <Card className={styles['d-widget']}>
        <CardHeader
          className={styles['d-widget-header']}
          title={<Typography variant="headline">{t('general:news_and_promotions')}</Typography>}
        />
        <Divider />
        <CardContent className={styles['d-widget-content']}>{this.renderCardContent()}</CardContent>
      </Card>
    );
  }
}
